/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/header.scss';

import React, {MouseEventHandler, useContext, useEffect, useRef, useState} from 'react';
import {AwesomeIcon, CloseButton, copyToClipboard, getMode} from '@dvrd/dvr-controls';
import {userHasSubscription} from "../../util/utils";
import userCircle from '@iconify/icons-fa-solid/user-circle';
import signOut from '@iconify/icons-fa/sign-out';
import {Icon} from '@iconify/react';
import {AppContext} from '../misc/appContext';
import HeaderCrumbs from "./crumbs/headerCrumbs";
import classNames from 'classnames';
import {UserType} from "../../util/interfaces";
import {hasAdminLogin} from "../../util/adminUtil";
import ContextAccountController from "../account/select/contextAccountController";
import {onShowConfirmSnackbar} from "../../util/renderUtil";
import {defer} from 'lodash';
import SystemMessagesController from "../systemMessage/systemMessagesController";
import Image from "../misc/image";
import IDate from '@dvrd/idate';

interface Props {
    onClickLogo: MouseEventHandler;
    onClickUpgrade: MouseEventHandler;
    onClickLogout: MouseEventHandler;
    onClickDemoRegister: MouseEventHandler;
    menuWidth: number;
    isDemo: boolean;
    upgradeClicked: boolean;
}

export default function Header(props: Props) {
    const {onClickLogo, onClickLogout, menuWidth, onClickUpgrade, onClickDemoRegister, isDemo, upgradeClicked} = props,
        context = useContext(AppContext),
        {user} = context.userContext,
        [headerHeight, setHeaderHeight] = useState(0),
        [adminStatsVisible, setAdminStatsVisible] = useState(true),
        headerRef = useRef<HTMLDivElement>(null),
        resizeObserver = useRef(new ResizeObserver(() => {
            determineHeaderHeight();
        }));

    function onDoubleClickEnv(evt: React.MouseEvent) {
        const label = evt.target as HTMLSpanElement;
        label.parentElement?.removeChild(label);
    }

    function onClickId(id: string) {
        return function () {
            copyToClipboard(id);
            onShowConfirmSnackbar(`${id} gekopieerd`);
        }
    }

    function onHideAdminStats() {
        setAdminStatsVisible(false);
    }

    function determineHeaderHeight() {
        const header = headerRef.current;
        if (!header) return;
        const subWarning = header.querySelector('div.sub-warning'),
            height = header.getBoundingClientRect().height + (subWarning?.getBoundingClientRect().height ?? 0);
        setHeaderHeight(height);
        const menu = document.getElementById('bolmate-menu');
        if (menu) {
            menu.style.height = `calc(100vh - ${height}px)`;
            menu.style.top = height + 'px';
        }
    }

    function attachListener() {
        const header = headerRef.current;
        if (!header) return;
        resizeObserver.current.observe(header);
    }

    function removeListener() {
        const header = headerRef.current;
        if (!header) return;
        resizeObserver.current.unobserve(header);
    }

    function renderEnv() {
        if (isDemo) return renderDemoEnv();
        const mode = getMode();
        let env: string | null = null;
        if (mode === 'dev') env = 'Develop omgeving';
        else if (mode === 'accept') env = 'Acceptatie omgeving';
        else if (['test', 'sub'].includes(mode)) env = 'Test omgeving';
        if (env) return <span className='env' onDoubleClick={onDoubleClickEnv}>{env}</span>;
        return null;
    }

    function renderDemoEnv() {
        return (
            <div className='demo-header'>
                <span className='demo-label'>Demo account</span>
                <div className='button-container'>
                    <div className={classNames('upgrade-btn', 'blue')}
                         onClick={onClickDemoRegister}>
                        {/*<Image name='rocket-2' className='rocket'/>*/}
                        {/*<img src={getImg('rocket-2')} alt='' className='rocket'/>*/}
                        <label className='btn-label'>Ja, ik probeer het nu voor 14 dagen gratis</label>
                    </div>
                    <label className='disclaimer'>Stopt automatisch, geen betaalgegevens nodig</label>
                </div>
            </div>
        )
    }

    function renderTrialCounter() {
        const {user} = context.userContext;
        if (!user?.subscription || userHasSubscription(user, false)) return null;
        const {trialEndDate} = user.subscription;
        const now = IDate.now();
        const showDays = trialEndDate?.isAtOrAfter(now, 'day');
        const daysLeft: number = Math.ceil(trialEndDate?.diff(now, 'd', true) ?? 0);
        const countColor = daysLeft < 5 ? 'red' : daysLeft < 10 ? 'orange' : 'green';
        return (
            <div className='trial-counter'>
                {!!showDays && <>
                    <label className='count-label'>Demo nog </label>
                    <span className={classNames('count', countColor)}>{daysLeft}</span>
                    <label className='count-label'> {daysLeft === 1 ? 'dag' : 'dagen'}</label>
                </>}
                <div className={classNames('upgrade-btn', upgradeClicked && 'inactive')} onClick={onClickUpgrade}>
                    <label className='btn-label'>Abonnement starten </label>
                    <AwesomeIcon name='arrow-right'/>
                </div>
            </div>
        )
    }

    function renderAdminStats() {
        const {user} = context.userContext, {account} = context.accountContext;
        if (!user || (!hasAdminLogin() && user.type !== UserType.SUPER_ADMIN) || !adminStatsVisible) return null;
        const apiKey = account?.getAPIKey() ?? null;
        return (
            <div className='admin-stats'>
                <CloseButton onClick={onHideAdminStats} className='hide-button'/>
                <>
                    <label className='key'>Gebruiker:</label>
                    <label onClick={onClickId(user.id)} className='click' title='Kopiëren'>{user.id}</label>
                </>
                {account !== null && (
                    <>
                        <label className='key'>Account:</label>
                        <label onClick={onClickId(account.id)} className='click' title='Kopiëren'>{account.id}</label>
                    </>
                )}
                {apiKey !== null && (
                    <>
                        <label className='key'>API key:</label>
                        <label onClick={onClickId(apiKey.id)} className='click' title='Kopiëren'>{apiKey.id}</label>
                    </>
                )}
            </div>
        )
    }

    useEffect(() => {
        defer(() => {
            attachListener();
            determineHeaderHeight();
        });
        return function () {
            removeListener();
        }
    }, []);

    return (
        <>
            <div id='invisible-header' style={{height: `${headerHeight}px`}}/>
            <div className='bolmate-header' ref={headerRef}>
                <SystemMessagesController/>
                <div className='header-content'>
                    <div className='logo-container' style={{width: `${menuWidth}px`}}>
                        <Image name='logo' className='logo' onClick={onClickLogo} onLoad={determineHeaderHeight}/>
                    </div>
                </div>
                <div className='header-content between'>
                    <HeaderCrumbs/>
                    <div style={{display: 'flex'}}>
                        {renderEnv()}
                        {renderTrialCounter()}
                        {renderAdminStats()}
                    </div>
                    {user !== null && <div className='user-container'>
                        <ContextAccountController/>
                        <Icon icon={userCircle} className='user-icon'/>
                        <label>{user.fullName}</label>
                        <div className='sign-out-btn' onClick={onClickLogout}>
                            <Icon icon={signOut}/>
                        </div>
                    </div>}
                </div>
            </div>
        </>
    )
}
